import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import styles from "./index.module.css";
import { checkHasPassword } from "slices/password";
import { RootState } from "state";
import Overview from "features/Overview";
import Customers from "features/Customers";
import { fetchVersion, setIsOnline } from "../slices/version";
import { purge as purgeMenu } from "../slices/menu";
import { purge as purgeTableOrders } from "../slices/tableOrders";
import { purge as purgeOrders } from "../slices/orders";
import { purge as purgeTables } from "../slices/tables";
import { purge as purgeWaiters } from "../slices/waiters";
import { purge as purgeWaiterCalls } from "../slices/waiterCall";
import Refresh from "../features/Refresh";
import useLiveVersion from "../helpers/useLiveVersion";
import { useTranslation } from "react-i18next";
import { fetchProfile } from "../slices/profileSlice";
import { fetchBranches } from "../slices/dashboardFilters";

export const PrivateRoutes: React.FC = () => {
  const dispatch = useDispatch();

  useLiveVersion();

  const { t } = useTranslation();

  const { version, isOnline } = useSelector(
    (state: RootState) => state.version,
  );
  const { branchFilter } = useSelector(
    (state: RootState) => state.dashboardFilters,
  );

  const initializeApp = useCallback(async () => {
    await dispatch(checkHasPassword());
    await dispatch(fetchVersion());

    // Fetch branches first (will skip if already loaded)
    await dispatch(fetchBranches());

    // After fetching branches, fetch profiles for all branches or the selected ones
    if (branchFilter && branchFilter.length > 0) {
      await dispatch(fetchProfile(branchFilter));
    } else {
      await dispatch(fetchProfile());
    }
  }, [dispatch, branchFilter]);

  useEffect(() => {
    initializeApp();

    // Force reload when back online
    const reloadWhenBackOnline = () => {
      dispatch(setIsOnline(true));
      document.location.reload();
    };

    window.addEventListener("online", reloadWhenBackOnline);

    return () => {
      window.removeEventListener("online", reloadWhenBackOnline);
    };
  }, [dispatch, initializeApp]);

  // Set marker when offline
  useEffect(() => {
    const markOffline = () => {
      dispatch(setIsOnline(false));
    };
    window.addEventListener("offline", markOffline);
    return () => {
      window.removeEventListener("offline", markOffline);
    };
  }, [dispatch]);

  const correctVersion = process.env.REACT_APP_VERSION === version.version;

  useEffect(() => {
    // Force empty reducers if version has been updated
    if (!correctVersion) {
      dispatch(purgeMenu());
      dispatch(purgeOrders());
      dispatch(purgeTableOrders());
      dispatch(purgeTables());
      dispatch(purgeWaiterCalls());
      dispatch(purgeWaiters());
    }
  }, [dispatch, correctVersion]);

  if (!isOnline) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>{t("common.Offline.title")}</div>
        <div>{t("common.Offline.content")}</div>
      </div>
    );
  }

  return (
    <Router>
      {correctVersion && (
        <Switch>
          <Route exact path="/overview" component={Overview} />
          <Route exact path="/customers" component={Customers} />

          <Redirect exact from="/" to="/overview" />
          <Redirect exact from="/login" to="/overview" />
          <Redirect from="*" to="/overview" />
        </Switch>
      )}
      {!correctVersion && (
        <Switch>
          <Route path="*" component={Refresh} />
        </Switch>
      )}
    </Router>
  );
};

export default PrivateRoutes;
